import React, { useState, useEffect, useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import useSWR from 'swr'
import Select from 'react-select'
import { fetchMatches } from '../services/api'
import { Badge, Button, FormattedDateTime, Pagination, CompetitorSelect, CompetitionSelect, VenueSelect } from '../components'
import { useMatchesFilters, useCopyToClipboard } from '../hooks'
import { formatMatchStatus } from '../utils/format'
import { CATEGORY_OPTIONS, PUBLISHING_STATUS_OPTIONS } from '../constants'
import type { Venue } from '../types/forms'
import pageStyles from '../styles/page.module.scss'
import listTableStyles from '../styles/list-table.module.scss'
import selectStyles from '../styles/react-select.module.scss'
import buttonStyles from '../components/Button.module.scss'
import styles from './MatchesList.module.scss'

const MatchesList: React.FC = () => {
  const navigate = useNavigate()

  // Get filters from URL state
  const { filters, updateFilterAndResetPage, handlePageChange } = useMatchesFilters()
  const { startDate, endDate, playerIds, competitionIds, courtIds, publishingStatuses, categories, search, includeUnlicensed, sortOrder, page } = filters

  // Keep limit as local state (not typically in URL)
  const [limit] = useState(100)

  // Clipboard copy for Publishing ID
  const { copiedId: copiedMatchId, handleCopy: handleCopyPublishingId } = useCopyToClipboard()

  // Local state for the full objects (selects need full objects for display)
  // Populated from applied_filters in API response
  const [selectedPlayers, setSelectedPlayers] = useState<any[]>([])
  const [selectedCompetitions, setSelectedCompetitions] = useState<any[]>([])
  const [selectedCourts, setSelectedCourts] = useState<Venue[]>([])

  // Fetch matches (uses v_match view with pre-coalesced values)
  const { data, isLoading, error, mutate } = useSWR(
    ['matches', { startDate, endDate, playerIds, competitionIds, courtIds, publishingStatuses, categories, search, includeUnlicensed, sortOrder, page, limit }],
    () => fetchMatches({
      start_date: startDate,
      end_date: endDate,
      player_ids: playerIds.length > 0 ? playerIds : undefined,
      competition_ids: competitionIds.length > 0 ? competitionIds : undefined,
      categories: categories.length > 0 ? categories : undefined,
      publishing_statuses: publishingStatuses.length > 0 ? publishingStatuses : undefined,
      venue_ids: courtIds.length > 0 ? courtIds : undefined,
      search: search || undefined,
      include_unlicensed: includeUnlicensed || undefined,
      sort_order: sortOrder,
      page,
      limit
    }),
    { revalidateOnFocus: false }
  )



  // Parse data safely (handle error case)
  const matchesData: any = data?.body ? JSON.parse(data.body) : data
  const matches: any[] = useMemo(() => matchesData?.matches || [], [matchesData])
  const pagination = matchesData?.pagination
  const appliedFilters = matchesData?.applied_filters

  // Update selected players from API response (applied_filters)
  useEffect(() => {
    if (appliedFilters?.players) {
      setSelectedPlayers(appliedFilters.players)
    } else if (playerIds.length === 0) {
      // Clear selected players if no player filters
      setSelectedPlayers([])
    }
  }, [appliedFilters, playerIds.length])

  // Update selected competitions from API response (applied_filters)
  useEffect(() => {
    if (appliedFilters?.competitions) {
      // Transform to CompetitionFilter format
      // Note: competition_ids in filters are parent IDs, so use tc_competition_parent_id
      const competitions = appliedFilters.competitions.map((c: any) => ({
        tc_id: c.tc_competition_parent_id,
        name: c.tc_name || c.ext_name || 'Unknown Competition'
      }))
      setSelectedCompetitions(competitions)
    } else if (competitionIds.length === 0) {
      // Clear selected competitions if no competition filters
      setSelectedCompetitions([])
    }
  }, [appliedFilters, competitionIds.length])

  // Update selected courts from match data (derive venue objects from results)
  useEffect(() => {
    if (courtIds.length > 0 && selectedCourts.length === 0 && matches.length > 0) {
      const courtIdSet = new Set(courtIds)
      const venueMap = new Map<string, Venue>()
      for (const match of matches) {
        if (match.venue && courtIdSet.has(match.venue.tc_venue_id)) {
          venueMap.set(match.venue.tc_venue_id, {
            tc_id: match.venue.tc_venue_id,
            ext_id: '',
            name: match.venue.name || 'Unknown Court',
            city: match.venue.city || '',
          })
        }
      }
      if (venueMap.size > 0) {
        setSelectedCourts(Array.from(venueMap.values()))
      }
    } else if (courtIds.length === 0) {
      setSelectedCourts([])
    }
  }, [courtIds, matches, selectedCourts.length])

  // Helper functions - v2 provides pre-coalesced values
  const getCompetitorName = (competitor: any) => {
    if (!competitor) return 'TBD'
    return competitor.name || 'Unknown'
  }

  const getVenueName = (venue: any) => {
    if (!venue) return 'N/A'
    const { name, city } = venue
    return city ? `${name}, ${city}` : (name || 'Unknown')
  }

  const getCompetitionName = (competition: any) => {
    if (!competition) return 'N/A'
    return competition.name || 'Unknown'
  }

  // Early return for error state (after all hooks)
  if (error) {
    return (
      <div className={pageStyles.pageContainer}>
        <div className={pageStyles.error}>
          <h2>Error loading matches</h2>
          <p>Failed to fetch matches: {(error as Error).message}</p>
          <Button variant="danger" onClick={() => mutate()}>Retry</Button>
        </div>
      </div>
    )
  }

  return (
    <div className={pageStyles.pageContainer}>
      <div className={pageStyles.pageHeader}>
        <h1>Matches</h1>
        <div className={pageStyles.headerButtons}>
          <Button onClick={() => navigate('/matches/new')}>
            + Create Match
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className={listTableStyles.searchSection}>
        <div className={listTableStyles.searchForm}>
          {/* Date Range and Search Filters */}
          <div className={styles.dateFilters}>
            <div className={styles.dateField}>
              <label htmlFor="startDate">Start Date:</label>
              <input
                type="date"
                id="startDate"
                value={startDate}
                onChange={(e) => updateFilterAndResetPage({ startDate: e.target.value })}
                className={listTableStyles.searchInput}
              />
            </div>
            <div className={styles.dateField}>
              <label htmlFor="endDate">End Date:</label>
              <input
                type="date"
                id="endDate"
                value={endDate}
                onChange={(e) => updateFilterAndResetPage({ endDate: e.target.value })}
                className={listTableStyles.searchInput}
              />
            </div>
            <div className={styles.dateField}>
              <label htmlFor="search">Search by ID:</label>
              <input
                type="text"
                id="search"
                value={search}
                onChange={(e) => updateFilterAndResetPage({ search: e.target.value })}
                placeholder="tc_match_id, ext_match_id, or tc_pub_content_id"
                className={listTableStyles.searchInput}
              />
            </div>
          </div>

          {/* Additional Filters - Row 1: Players, Categories, Publishing Status */}
          <div className={styles.filterRow}>
            <div className={styles.filterField}>
              <label>Players</label>
              <CompetitorSelect
                teamType="singles"
                isMulti
                value={selectedPlayers.length > 0 ? selectedPlayers : null}
                onChange={(value) => {
                  const players = Array.isArray(value) ? value : (value ? [value] : [])
                  setSelectedPlayers(players)
                  const ids = players.map((p: any) => p.tc_id)
                  updateFilterAndResetPage({ playerIds: ids })
                }}
              />
            </div>

            <div className={styles.filterField}>
              <label>Categories</label>
              <Select
                isMulti
                isSearchable
                isClearable
                placeholder="Select categories..."
                options={CATEGORY_OPTIONS}
                value={categories.map(cat => ({
                  value: cat,
                  label: cat
                }))}
                onChange={(selectedOptions) => {
                  const selectedCategories = selectedOptions ? selectedOptions.map(option => option.value) : []
                  updateFilterAndResetPage({ categories: selectedCategories })
                }}
                className={selectStyles.reactSelect}
                classNamePrefix="react-select"
              />
            </div>

            <div className={styles.filterField}>
              <label>Publishing Status</label>
              <Select
                isMulti
                isSearchable
                isClearable
                placeholder="Select publishing statuses..."
                options={PUBLISHING_STATUS_OPTIONS}
                value={PUBLISHING_STATUS_OPTIONS.filter(opt =>
                  publishingStatuses.includes(opt.value)
                )}
                onChange={(selectedOptions) => {
                  const statuses = selectedOptions
                    ? selectedOptions.map(opt => opt.value)
                    : []
                  updateFilterAndResetPage({ publishingStatuses: statuses })
                }}
                className={selectStyles.reactSelect}
                classNamePrefix="react-select"
              />
            </div>
          </div>

          {/* Additional Filters - Row 2: Competition, Court, Unlicensed */}
          <div className={`${styles.filterRow} ${styles.filterRowWithToggle}`}>
            <div className={styles.filterField}>
              <label>Competitions</label>
              <CompetitionSelect
                isMulti
                value={selectedCompetitions.length > 0 ? selectedCompetitions : null}
                onChange={(value) => {
                  const competitions = Array.isArray(value) ? value : (value ? [value] : [])
                  setSelectedCompetitions(competitions)
                  const ids = competitions.map((c: any) => c.tc_id)
                  updateFilterAndResetPage({ competitionIds: ids })
                }}
              />
            </div>

            <div className={styles.filterField}>
              <label>Court</label>
              <VenueSelect
                isMulti
                value={selectedCourts.length > 0 ? selectedCourts : null}
                onChange={(value) => {
                  const courts = Array.isArray(value) ? value : (value ? [value] : [])
                  setSelectedCourts(courts)
                  const ids = courts.map((v: Venue) => v.tc_id)
                  updateFilterAndResetPage({ courtIds: ids })
                }}
              />
            </div>

            <Badge
              variant={includeUnlicensed ? 'warning' : 'neutral'}
              className={styles.licenseToggle}
              clickable
              ariaPressed={includeUnlicensed}
              ariaLabel="Include matches from unlicensed seasons"
              icon={includeUnlicensed ? 'check' : undefined}
              onClick={() => updateFilterAndResetPage({ includeUnlicensed: !includeUnlicensed })}
            >
              Unlicensed
            </Badge>
          </div>
        </div>
      </div>

      {isLoading && !data ? (
        <div className={pageStyles.loading}>
          <h3>Loading matches...</h3>
          <p>Please wait while we fetch the match data.</p>
        </div>
      ) : matches.length === 0 ? (
        <div className={listTableStyles.noResults}>
          <h3>No matches found</h3>
          <p>No matches are available for the selected date range.</p>
          {!includeUnlicensed && (
            <p>Unlicensed seasons are excluded &mdash; toggle &quot;Unlicensed&quot; to include them.</p>
          )}
        </div>
      ) : (
        <>
          <div className={`${listTableStyles.tableHeader} ${styles.listHeader}`}>
            <div>Match</div>
            <div>Competition</div>
            <div>Court</div>
            <div>Date/Time</div>
            <div>Status</div>
            <div>Publishing</div>
            <div></div>
          </div>

          <div className={listTableStyles.tableBody}>
            {matches.map((match) => {
              // v2 provides effective_* fields (pre-coalesced)
              const { effective_round, effective_status, effective_start_time, season_is_licensed } = match.match

              return (
                <div key={match.match.tc_match_id} className={`${listTableStyles.tableRow} ${styles.listItem}`}>
                  <div className={listTableStyles.cellPrimary}>
                    <p className={listTableStyles.primaryText}>
                      {getCompetitorName(match.home_competitor)} vs {getCompetitorName(match.away_competitor)}
                    </p>
                    {effective_round && (
                      <p className={listTableStyles.secondaryText}>{effective_round}</p>
                    )}
                  </div>

                  <div className={listTableStyles.cellText}>
                    {getCompetitionName(match.competition)}
                  </div>

                  <div className={listTableStyles.cellText}>
                    {getVenueName(match.venue)}
                  </div>

                  <div className={listTableStyles.cellText}>
                    <FormattedDateTime dateString={effective_start_time} />
                  </div>

                  <div className={listTableStyles.cellBadges}>
                    {effective_status && (
                      <Badge variant="neutral">{formatMatchStatus(effective_status)}</Badge>
                    )}
                    {/* Keyed on the row, not the toggle: a match whose season
                        never resolved also reports as unlicensed. */}
                    {season_is_licensed === false && (
                      <Badge variant="warning" size="small">Unlicensed</Badge>
                    )}
                  </div>

                  <div className={listTableStyles.cellBadges}>
                    {match.publishing_status && (
                      <Badge
                        variant={
                          match.publishing_status === 'PUBLISHED' ? 'success' :
                            match.publishing_status === 'SCHEDULED' ? 'warning' :
                              match.publishing_status === 'DRAFT' ? 'neutral' :
                                'secondary'
                        }
                        clickable={!!match.publishing_id}
                        onClick={() => match.publishing_id && handleCopyPublishingId(match.match.tc_match_id, match.publishing_id)}
                        icon={match.publishing_id ? (copiedMatchId === match.match.tc_match_id ? 'check' : 'clipboard') : undefined}
                        ariaLabel={match.publishing_id ? `Copy Publishing ID ${match.publishing_id}` : undefined}
                      >
                        {copiedMatchId === match.match.tc_match_id ? 'Copied!' : match.publishing_status.charAt(0) + match.publishing_status.slice(1).toLowerCase()}
                      </Badge>
                    )}
                  </div>

                  <div className={`${listTableStyles.cellActions} ${styles.actionsCell}`}>
                    <Link
                      to={`/matches/${match.match.tc_match_id}`}
                      className={`${buttonStyles.button} ${buttonStyles.primary} ${buttonStyles.medium}`}
                    >
                      View
                    </Link>
                  </div>
                </div>
              )
            })}
          </div>

          {pagination && (
            <Pagination
              pagination={pagination}
              onPageChange={handlePageChange}
              itemName="matches"
            />
          )}
        </>
      )}
    </div>
  )
}

export default MatchesList

